import axios from 'axios'

import { TaskStatusEnum } from '../components/TaskList/TaskList'
import { PaginatedResults } from '../helpers/typeHelper'
import { toCamelCase } from '../helpers/utils'
import { Colors } from '../theme/colors'
import { SlimTruck } from './truck'
import { ListUser } from './user'

export enum LeasingPartnerName {
  HEC_LEASING = 'HEC_LEASING',
  LRM_LEASING = 'LRM_LEASING',
  LONE_MOUNTAIN = 'LONE_MOUNTAIN',
  SFI = 'SFI',
  EXCELERATE = 'EXCELERATE',

  TEL = 'TEL',
  NA = 'NA',
}

export const LeasingPartnerNameLabel = {
  [LeasingPartnerName.HEC_LEASING]: 'HEC Leasing',
  [LeasingPartnerName.LRM_LEASING]: 'LRM Leasing',
  [LeasingPartnerName.LONE_MOUNTAIN]: 'Lone Mountain',
  [LeasingPartnerName.SFI]: 'SFI',
  [LeasingPartnerName.EXCELERATE]: 'Excelete',

  [LeasingPartnerName.TEL]: 'TEL',
  [LeasingPartnerName.NA]: 'NA',
}

export interface ListLeasingPartner {
  id: string
  application: {
    id: string
    eligibilityStatus: LeasingPartnerApplicationEligibilityStatus
    truckAssignmentStatus: LeasingPartnerApplicationTruckAssignmentStatus
    leaseStartDate: string | null
  }
  user: {
    id: string
    firstName: string
    middleName: string
    lastName: string
    email: string
  }
}

export interface LeasingPartner {
  id: string
  dateCreated: string
  dateUpdated: string
  dateApplied: string | null
  partnerName: LeasingPartnerName | null
  suggestedPartnerName: LeasingPartnerName | null
  availableDownPayment: string | null
  application: LeasingPartnerApplication | null
  user: ListUser | null
}

export enum LeasingPartnerApplicationEligibilityStatus {
  NA = 'NA',
  CONDITIONAL = 'CONDITIONAL',
  FULL = 'FULL',
}

export const LeasingPartnerApplicationEligibilityStatusLabel = {
  [LeasingPartnerApplicationEligibilityStatus.NA]: 'N/A',
  [LeasingPartnerApplicationEligibilityStatus.CONDITIONAL]: 'Conditional',
  [LeasingPartnerApplicationEligibilityStatus.FULL]: 'Full',
}

export enum LeasingPartnerApplicationTruckAssignmentStatus {
  NOT_STARTED = 'NOT_STARTED',
  DEPOSIT_REQUESTED = 'DEPOSIT_REQUESTED',
  READY_TO_BE_ASSIGNED = 'READY_TO_BE_ASSIGNED',
  PENDING_LEASE_APPLICATION = 'PENDING_LEASE_APPLICATION',
  PENDING_LEASE_SIGNING = 'PENDING_LEASE_SIGNING',
  COMPLETED = 'COMPLETED',
}

export const LeasingPartnerApplicationTruckAssignmentStatusLabel = {
  [LeasingPartnerApplicationTruckAssignmentStatus.NOT_STARTED]: 'Not started',
  [LeasingPartnerApplicationTruckAssignmentStatus.DEPOSIT_REQUESTED]: 'Deposit Requested',
  [LeasingPartnerApplicationTruckAssignmentStatus.READY_TO_BE_ASSIGNED]: 'Pending assignment',
  [LeasingPartnerApplicationTruckAssignmentStatus.PENDING_LEASE_APPLICATION]: 'Lease not generated',
  [LeasingPartnerApplicationTruckAssignmentStatus.PENDING_LEASE_SIGNING]: 'Lease generated, not signed',
  [LeasingPartnerApplicationTruckAssignmentStatus.COMPLETED]: 'Completed',
}

export const LeasingPartnerApplicationTruckAssignmentStatusColor = {
  [LeasingPartnerApplicationTruckAssignmentStatus.NOT_STARTED]: [],
  [LeasingPartnerApplicationTruckAssignmentStatus.DEPOSIT_REQUESTED]: [],
  [LeasingPartnerApplicationTruckAssignmentStatus.READY_TO_BE_ASSIGNED]: [
    Colors.warning,
    Colors.marigoldOrange,
  ],
  [LeasingPartnerApplicationTruckAssignmentStatus.PENDING_LEASE_APPLICATION]: [
    Colors.warning,
    Colors.marigoldOrange,
  ],
  [LeasingPartnerApplicationTruckAssignmentStatus.PENDING_LEASE_SIGNING]: [
    Colors.warning,
    Colors.marigoldOrange,
  ],
  [LeasingPartnerApplicationTruckAssignmentStatus.COMPLETED]: [Colors.success, Colors.kellyGreenD1],
}

export enum LeaseWarranty {
  WARRANTY_V1 = 'WARRANTY_V1',
  WARRANTY_V2 = 'WARRANTY_V2',
  WARRANTY_V3 = 'WARRANTY_V3',
}

export interface LeasingPartnerApplication {
  id: string
  dateCreated: string
  dateUpdated: string
  eligibilityStatus: LeasingPartnerApplicationEligibilityStatus
  truckAssignmentStatus: LeasingPartnerApplicationTruckAssignmentStatus
  truck?: SlimTruck
  leaseStartDate: string | null
  leaseTermsMonths: number | null
  leaseEndDate: string | null
  deductionsStartDate: string | null
  weeklyMaintenanceReserve: number | null
  weeklyTruckNotePayment: number | null
  weeklyPhysicalDamageInsurance: number | null
  downPayment: LeasingPartnerApplicationDownPayment | null
  weeklyTotalPayment: number
  dqEngine: {
    clearinghouse: TaskStatusEnum
    drugScreen: TaskStatusEnum
    drivingHistory: TaskStatusEnum
    criminalRecordCheck: TaskStatusEnum
  }
  metadata: {
    leaseGuarantor: boolean | null
  }
  warranty: LeaseWarranty
  warrantyLabel: string
  warrantyOptions: { value: LeaseWarranty; label: string }[]
}

export enum LeasingPartnerApplicationDownPaymentStatus {
  OPEN = 'OPEN',
  COMPLETE = 'COMPLETE',
  EXPIRED = 'EXPIRED',
  PROCESSING = 'PROCESSING',
  REQUIRES_ACTION = 'REQUIRES_ACTION',
  ERROR = 'ERROR',
}

export interface LeasingPartnerApplicationDownPayment {
  id: string
  dateCreated: string
  dateUpdated: string
  amount: number | null
  status: LeasingPartnerApplicationDownPaymentStatus
  stripeSessionId: string | null
  stripeNextAction: any
}

export interface LeasingPartnerConfig {
  name: LeasingPartnerName
  label: string
  payment: string
  description: string
  isPreferred: boolean
  isEnabledInApp: boolean
  features: string[]
  highlight: string
  url: string
  inventoryUrl: string
}

export function getLeasingPartners(
  page = 1,
  truckAssignmentStatus?: LeasingPartnerApplicationTruckAssignmentStatus[] | null,
  driverName?: string
): Promise<PaginatedResults<ListLeasingPartner>> {
  return axios
    .get(
      `/api/v3/leasing-partner/?page=${page}${
        truckAssignmentStatus && truckAssignmentStatus.length
          ? `&truck_assignment_status=${truckAssignmentStatus.join(',')}`
          : ''
      }${driverName ? `&driver_name=${driverName}` : ''}`
    )
    .then((response) => {
      return {
        ...response.data,
        results: response.data.results.map(toCamelCase),
      } as PaginatedResults<ListLeasingPartner>
    })
}

export function getLeasingPartner(id: string): Promise<LeasingPartner> {
  return axios
    .get(`/api/v3/leasing-partner/${id}/`)
    .then((response) => toCamelCase(response.data)) as Promise<LeasingPartner>
}

export function getLeasingPartnerByTruck(truckId: string): Promise<LeasingPartner | null> {
  return axios
    .get(`/api/v3/leasing-partner/get-by-truck/?truck_id=${truckId}`)
    .then((response) => toCamelCase(response.data))
    .catch(() => null) as Promise<LeasingPartner | null>
}

export function getActiveLeasingPartner(partnerName?: LeasingPartnerName): Promise<LeasingPartner> {
  return axios
    .get(`/api/v3/leasing-partner/active/${partnerName ? `?partner_name=${partnerName}` : ''}`)
    .then((response) => toCamelCase(response.data)) as Promise<LeasingPartner>
}

export function getLeasingPartnerConfig(): Promise<LeasingPartnerConfig[]> {
  return axios
    .get(`/api/v3/leasing-partner/config/`)
    .then((response) => response.data.map(toCamelCase) as LeasingPartnerConfig[])
}

export function applyToLeasingPartner(partnerName: LeasingPartnerName) {
  return axios.post(`/api/v3/leasing-partner/apply/`, { partner_name: partnerName })
}

export function assignTruck(id: string, data: Record<string, any>) {
  return axios.post(`/api/v3/leasing-partner/${id}/assign-truck/`, data)
}

export function sendTruckLeasingAgreement(id: string, data: Record<string, any>) {
  return axios.post(`/api/v3/leasing-partner/${id}/send-truck-leasing-agreement/`, data)
}

export function unassignTruck(id: string) {
  return axios.post(`/api/v3/leasing-partner/${id}/unassign-truck/`)
}
